<template>
    <nav class="ds-settings-nav" aria-label="Разделы настроек">
        <div class="ds-settings-nav-title">Настройки</div>

        <label class="ds-settings-nav-search">
            <SettingsIcon name="search" :size="16" :stroke-width="2" />
            <input
                v-model="query"
                type="search"
                placeholder="Найти настройку"
                aria-label="Найти настройку"
                autocomplete="off"
                @keydown.esc="query = ''"
            >
        </label>

        <!-- Результаты поиска заменяют меню; пустой запрос возвращает обычное меню. -->
        <div v-if="query.trim()" class="ds-settings-search-results" role="list" aria-label="Результаты поиска">
            <p v-if="!results.length" class="ds-settings-search-empty">Ничего не найдено</p>
            <div v-for="item in results" :key="item.id" class="ds-settings-search-item" role="listitem">
                <button type="button" class="ds-settings-search-main" :disabled="editor.saving.value" @click="open(item.target)">
                    <span class="ds-settings-search-label">{{ item.label }}</span>
                    <span v-if="item.techKey" class="ds-settings-search-key">{{ item.techKey }}</span>
                    <span class="ds-settings-search-context">{{ item.context }}</span>
                </button>
                <div v-if="item.roles?.length" class="ds-settings-search-roles">
                    <button
                        v-for="role in item.roles"
                        :key="role.id"
                        type="button"
                        class="ds-settings-search-role"
                        :disabled="editor.saving.value"
                        @click="open({ ...item.target, roleId: role.id })"
                    >{{ role.name }}</button>
                </div>
            </div>
        </div>

        <div v-else class="ds-settings-nav-list">
            <template v-for="item in sections" :key="item.id">
                <button
                    type="button"
                    class="ds-settings-nav-item"
                    :class="{ 'is-active': editor.section.value === item.id }"
                    :disabled="!editor.sectionEnabled(item.id) || (editor.saving.value && editor.section.value !== item.id)"
                    :aria-current="editor.section.value === item.id ? 'page' : undefined"
                    @click="editor.navigate({ section: item.id })"
                >
                    <SettingsIcon :name="item.icon" />
                    <span class="ds-settings-nav-label">{{ item.label }}</span>
                    <span
                        v-if="editor.sectionChangeCount(item.id)"
                        class="ds-settings-badge"
                        :aria-label="`Несохранённых правок: ${editor.sectionChangeCount(item.id)}`"
                    >{{ editor.sectionChangeCount(item.id) }}</span>
                </button>

                <template v-if="editor.section.value === item.id">
                    <button
                        v-for="block in blocksOf(item.id)"
                        :key="block.anchor"
                        type="button"
                        class="ds-settings-nav-sub"
                        :class="{ 'is-active': editor.activeAnchor.value === block.anchor }"
                        @click="editor.navigate({ section: item.id, anchor: block.anchor })"
                    >
                        <span class="ds-settings-nav-label">{{ block.label }}</span>
                        <span v-if="editor.blockHasChanges(block.label)" class="ds-settings-dot" aria-label="Есть несохранённые правки"></span>
                    </button>

                    <template v-if="item.id === 'roles'">
                        <p v-if="editor.load.roles === 'ready' && !editor.roles.value.length" class="ds-settings-nav-note">Ролей нет</p>
                        <button
                            v-for="role in editor.roles.value"
                            :key="role.id"
                            type="button"
                            class="ds-settings-nav-sub ds-settings-nav-role"
                            :class="{ 'is-active': editor.selectedRoleId.value === role.id }"
                            :disabled="editor.saving.value && editor.selectedRoleId.value !== role.id"
                            :aria-current="editor.selectedRoleId.value === role.id ? 'true' : undefined"
                            @click="editor.navigate({ section: 'roles', roleId: role.id })"
                        >
                            <span class="ds-settings-nav-label">{{ role.name }}</span>
                            <span v-if="editor.roleChangeList(role.id).length" class="ds-settings-dot" aria-label="Есть несохранённые правки"></span>
                        </button>
                    </template>
                </template>
            </template>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import SettingsIcon from './SettingsIcon.vue'
import { useSettingsEditor } from './useSettingsEditor'
import { SECTION_BLOCKS, SECTION_LABELS, buildSettingsSearchIndex, searchSettings, type SettingsNavTarget } from './settingsSearch'
import type { SettingsSectionId } from './settingsModel'

const editor = useSettingsEditor()
const query = ref('')

const sections: Array<{ id: SettingsSectionId, label: string, icon: 'clock' | 'shield' | 'briefcase' | 'swap' }> = [
    { id: 'general', label: SECTION_LABELS.general, icon: 'clock' },
    { id: 'roles', label: SECTION_LABELS.roles, icon: 'shield' },
    { id: 'positions', label: SECTION_LABELS.positions, icon: 'briefcase' },
    { id: 'exchange', label: SECTION_LABELS.exchange, icon: 'swap' },
]

const blocksOf = (section: SettingsSectionId) => SECTION_BLOCKS[section]

// Только доступные разделы; роли — только уже разрешённые к загрузке.
const index = computed(() => buildSettingsSearchIndex(
    editor.availableSections.value,
    editor.load.roles === 'ready' ? editor.roles.value : [],
))
const results = computed(() => searchSettings(index.value, query.value))

async function open(target: SettingsNavTarget) {
    await editor.navigate(target)
    if (editor.section.value === target.section) query.value = ''
}
</script>
